import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { type ApiError, api, qs, unwrap } from '../api/client';
import type { Account } from '../api/types';
import { ErrorAlert, PageHeader } from '../components/ui';

interface PreviewAccount {
  code: string;
  externalId: string;
  username: string;
  name: string | null;
  accountType: string;
  profilePictureUrl: string | null;
}

/** UI-70: the fake Instagram's consent dialog; a real app goes to Instagram and never sees this page. */
export function ConnectConsentPage() {
  const [params] = useSearchParams();
  const state = params.get('state');
  const preview = useQuery({
    queryKey: ['connect-preview'],
    queryFn: () =>
      unwrap(
        api.get<{ accounts: PreviewAccount[]; permissions: string[] }>(
          '/portal/social-accounts/connect/preview',
        ),
      ),
  });
  const [code, setCode] = useState<string>('');
  const chosen = code || preview.data?.accounts[0]?.code || '';
  const connect = useMutation({
    // SOC-22: the fake answers `fail-code` with a refusal, so the failure path can be shown
    mutationFn: (withCode: string = chosen) =>
      unwrap(api.get<Account>(`/portal/social-accounts/callback${qs({ code: withCode, state })}`)),
  });
  return (
    <div className="auth-shell">
      <PageHeader
        title="Connect Instagram (fake dialog)"
        subtitle="This stand-in shows what Instagram asks: which account, and which permissions"
      />
      {state === null ? (
        <div className="alert alert-error">The connect link is missing its state; start again.</div>
      ) : null}
      <ErrorAlert error={preview.error as ApiError | null} />
      <ErrorAlert error={connect.error as ApiError | null} />
      {connect.data ? (
        <div className="alert alert-success">
          Connected @{connect.data.username}.{' '}
          <Link to="/app/accounts">Back to Social Accounts</Link>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-title">Choose the professional account</div>
            {(preview.data?.accounts ?? []).map((a) => (
              <label key={a.code} className="row" style={{ alignItems: 'center', gap: 10 }}>
                <input
                  type="radio"
                  name="account"
                  aria-label={`@${a.username}`}
                  checked={chosen === a.code}
                  onChange={() => setCode(a.code)}
                />
                {a.profilePictureUrl ? (
                  <img src={a.profilePictureUrl} alt="" width={32} height={32} />
                ) : null}
                <span>
                  <strong>{a.name ?? a.username}</strong>{' '}
                  <span className="muted">@{a.username}</span>{' '}
                  <span className="badge">{a.accountType}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="card">
            <div className="card-title">AutoDM asks for</div>
            <ul>
              {(preview.data?.permissions ?? []).map((p) => (
                <li key={p}>
                  <code>{p}</code>
                </li>
              ))}
            </ul>
          </div>
          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!chosen || state === null || connect.isPending}
              onClick={() => connect.mutate(chosen)}
            >
              Continue
            </button>
            <Link className="btn" to="/app/accounts">
              Cancel
            </Link>
            {preview.data ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={state === null || connect.isPending}
                onClick={() => connect.mutate('fail-code')}
                title="Runs the callback with a code the fake Instagram refuses"
              >
                Simulate a refused authorization
              </button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
